import { useParams, useNavigate } from 'react-router-dom'
import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
import { useQuery } from '@tanstack/react-query'
import { getStockItem } from '../../api/stock'
import { ApiError } from '../../api/client'
import { PageHeader } from '@/components/PageHeader'
import { DetailSection } from '@/components/DetailSection'

export default function StockDetail() {
  const { sku } = useParams<{ sku: string }>()
  const navigate = useNavigate()

  const { data: item, isLoading, isError, error } = useQuery({
    queryKey: ['stock', sku],
    queryFn: () => getStockItem(sku!),
    enabled: !!sku,
  })

  if (isLoading) {
    return (
      <div className="p-6 text-sm text-muted-foreground">Lade Artikel…</div>
    )
  }

  if (isError || !item) {
    return (
      <div className="p-6">
        <p className="text-sm text-destructive">
          {error instanceof ApiError ? error.message : 'Artikel nicht gefunden'}
        </p>
        <button
          onClick={() => navigate('/stock')}
          className="mt-3 text-sm text-blue-600 hover:underline"
        >
          ← Zurück zur Liste
        </button>
      </div>
    )
  }

  const locationFields = item.location_fields ?? []
  const locationFieldsLabel =
    locationFields.length > 0 ? locationFields.join(', ') : undefined

  return (
    <div className="p-6 max-w-2xl">
      <PageHeader
        title={item.name}
        breadcrumb={[{ label: 'Lager', href: '/stock' }]}
      />

      {item.merchant_sku && (
        <p className="-mt-2 mb-4 text-sm">
          <span className="text-muted-foreground">Händl.ArtNr.: </span>
          <GrundartikelLink code={item.merchant_sku} className="font-mono font-semibold" />
        </p>
      )}

      <DetailSection
        title="Stammdaten"
        rows={[
          { label: 'SKU', value: item.sku, copyable: true },
          { label: 'EAN / Barcode', value: item.barcode },
          { label: 'MPN', value: item.mpn },
          { label: 'Hersteller', value: item.manufacturer },
          { label: 'VK-Preis', value: item.price ? `${Number(item.price).toFixed(2)} €` : undefined },
          { label: 'MwSt.', value: item.tax ? `${(Number(item.tax) * 100).toFixed(0)} %` : undefined },
          { label: 'Bestand', value: item.quantity },
          { label: 'Reserviert', value: item.quantity_reserved },
          { label: 'Verfügbar', value: item.quantity_available },
          { label: 'Warnschwelle', value: item.quantity_alert },
          { label: 'Lagerfächer', value: locationFieldsLabel },
        ].filter((row) => row.value !== undefined && row.value !== '')}
      />

      {locationFields.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1">
          {locationFields.map((field, idx) => (
            <span
              key={`${idx}-${field}`}
              className="inline-flex items-center rounded-full border border-border
                         bg-muted px-2.5 py-0.5 text-xs font-mono"
            >
              {field}
            </span>
          ))}
        </div>
      )}

      <p className="mt-3 text-xs text-muted-foreground">
        Zuletzt geändert: {new Date(item.updated_at).toLocaleString('de-DE')}
      </p>
    </div>
  )
}
